// ─── Helpers shared by every real-API service file ──────────────────────────
// Unwraps the ApiResponse<T> envelope the .NET backend returns, and provides
// a consistent shape for paginated list endpoints.
import { ApiRequestError } from './ApiRequestError';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Array<{ code: string; message: string; field?: string }>;
}

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

/**
 * Base shape every list endpoint accepts. Concrete entity APIs extend this
 * with their own filter fields (e.g. `status`, `category`).
 */
export interface PagedQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

export const DEFAULT_PAGE_SIZE = 20;

/**
 * Strip out empty / undefined / null values so axios doesn't serialise them
 * as `?status=&category=`. The backend treats blank query-string values as
 * present-but-empty, which can break filters that null-check the param.
 */
export function buildListParams<T extends PagedQuery>(query: T): Record<string, unknown> {
  const out: Record<string, unknown> = {
    pageNumber: query.pageNumber ?? 1,
    pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
  };
  Object.entries(query).forEach(([k, v]) => {
    if (k === 'pageNumber' || k === 'pageSize') return;
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    out[k] = v;
  });
  return out;
}

/**
 * Empty-result helper so callers can short-circuit (e.g. on error) without
 * having to construct the PagedResult shape inline.
 */
export function emptyPage<T>(pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE): PagedResult<T> {
  return {
    items: [],
    pageNumber,
    pageSize,
    totalCount: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
  };
}

export function unwrap<T>(envelope: ApiResponse<T>): T {
  if (!envelope.success || envelope.data === undefined) {
    const message = envelope.message ?? 'Request failed';
    throw new ApiRequestError({
      message,
      code: envelope.errors?.[0]?.code ?? 'API_ERROR',
      statusCode: 0,
      details: envelope.errors
        ? Object.fromEntries(envelope.errors.map((e, i) => [e.field ?? `error_${i}`, [e.message]]))
        : undefined
    });
  }
  return envelope.data;
}
